import { Project, Criterion, ScheduleItem, EventSettings, Ballot, ProjectScoreSummary } from '../types';
import { DEFAULT_PROJECTS, DEFAULT_CRITERIA, DEFAULT_SCHEDULE, DEFAULT_SETTINGS } from '../data/defaultData';

const STORAGE_KEYS = {
  PROJECTS: 'nextventures_2026_projects',
  DELETED_DEFAULTS: 'nextventures_2026_deleted_defaults',
  CRITERIA: 'nextventures_2026_criteria',
  SCHEDULE: 'nextventures_2026_schedule',
  SETTINGS: 'nextventures_2026_settings',
  BALLOTS: 'nextventures_2026_ballots',
  VOTER_ID: 'nextventures_2026_voter_id',
  VOTER_NAME: 'nextventures_2026_voter_name',
  VOTER_EMAIL: 'nextventures_2026_voter_email',
  USER_NOTES: 'nextventures_2026_user_notes',
  DRAFT_SCORES: 'nextventures_2026_draft_scores',
  DRAFT_FEEDBACK: 'nextventures_2026_draft_feedback',
  VOTER_ROLE: 'nextventures_2026_voter_role',
};

export function getDeletedDefaultIds(): string[] {
  const data = localStorage.getItem(STORAGE_KEYS.DELETED_DEFAULTS);
  if (data) {
    try {
      return JSON.parse(data);
    } catch {
      // fallback
    }
  }
  return [];
}

export function saveDeletedDefaultIds(ids: string[]): void {
  localStorage.setItem(STORAGE_KEYS.DELETED_DEFAULTS, JSON.stringify(ids));
}

export function getStoredVoterEmail(): string {
  return localStorage.getItem(STORAGE_KEYS.VOTER_EMAIL) || '';
}

export function saveVoterEmail(email: string): void {
  localStorage.setItem(STORAGE_KEYS.VOTER_EMAIL, email.trim().toLowerCase());
}

// Generate unique voter ID, deterministically derived from email when available
export function getOrCreateVoterId(email?: string): string {
  const targetEmail = (email || getStoredVoterEmail()).trim().toLowerCase();
  if (targetEmail) {
    const clean = targetEmail.replace(/[^a-z0-9@._-]/g, '_');
    const id = `voter_${clean}`;
    localStorage.setItem(STORAGE_KEYS.VOTER_ID, id);
    return id;
  }
  let id = localStorage.getItem(STORAGE_KEYS.VOTER_ID);
  if (!id) {
    id = 'voter_' + Math.random().toString(36).substring(2, 9) + '_' + Date.now().toString(36);
    localStorage.setItem(STORAGE_KEYS.VOTER_ID, id);
  }
  return id;
}

export function getStoredVoterName(): string {
  return localStorage.getItem(STORAGE_KEYS.VOTER_NAME) || '';
}

export function saveVoterName(name: string): void {
  localStorage.setItem(STORAGE_KEYS.VOTER_NAME, name);
}

export function getStoredVoterRole(): 'audience' | 'staff' {
  const role = localStorage.getItem(STORAGE_KEYS.VOTER_ROLE);
  return role === 'staff' ? 'staff' : 'audience';
}

export function saveVoterRole(role: 'audience' | 'staff'): void {
  localStorage.setItem(STORAGE_KEYS.VOTER_ROLE, role);
}

// Load / Save Settings
export function loadSettings(): EventSettings {
  const data = localStorage.getItem(STORAGE_KEYS.SETTINGS);
  if (data) {
    try {
      const parsed = JSON.parse(data);
      const eventDate = (!parsed.eventDate || parsed.eventDate === 'Thursday, October 15, 2026' || parsed.eventDate === 'October 15–16, 2026')
        ? DEFAULT_SETTINGS.eventDate
        : parsed.eventDate;
      const venue = (!parsed.venue || parsed.venue === 'Main Sanctuary & Fellowship Hall')
        ? DEFAULT_SETTINGS.venue
        : parsed.venue;
      const programBookUrl = (!parsed.programBookUrl || parsed.programBookUrl.trim() === '')
        ? DEFAULT_SETTINGS.programBookUrl
        : parsed.programBookUrl;
      return {
        ...DEFAULT_SETTINGS,
        ...parsed,
        eventDate,
        venue,
        programBookUrl,
        adminPin: (!parsed.adminPin || parsed.adminPin === '7777') ? '1616' : parsed.adminPin,
        phase: 'VOTING_OPEN',
      };
    } catch {
      // fallback
    }
  }
  return DEFAULT_SETTINGS;
}

export function saveSettings(settings: EventSettings): void {
  localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
}

// Load / Save Projects
export function loadProjects(): Project[] {
  const data = localStorage.getItem(STORAGE_KEYS.PROJECTS);
  if (data) {
    try {
      const parsed: Project[] = JSON.parse(data);
      if (Array.isArray(parsed)) {
        const deletedIds = new Set(getDeletedDefaultIds());
        const existingIds = new Set(parsed.map((p) => p.id));

        // Find default projects that are missing and have NOT been explicitly confirmed deleted
        const missingDefaults = DEFAULT_PROJECTS.filter(
          (dp) => !existingIds.has(dp.id) && !deletedIds.has(dp.id)
        );

        if (missingDefaults.length > 0) {
          // Restore missing defaults, maintaining order
          const restored = [...parsed, ...missingDefaults].sort((a, b) => {
            const orderA = DEFAULT_PROJECTS.findIndex((dp) => dp.id === a.id);
            const orderB = DEFAULT_PROJECTS.findIndex((dp) => dp.id === b.id);
            if (orderA !== -1 && orderB !== -1) return orderA - orderB;
            if (orderA !== -1) return -1;
            if (orderB !== -1) return 1;
            return (a.order || 0) - (b.order || 0);
          }).map((p, idx) => ({ ...p, order: idx + 1 }));

          saveProjects(restored);
          return restored;
        }
        return parsed;
      }
    } catch {
      // fallback
    }
  }
  return DEFAULT_PROJECTS;
}

export function saveProjects(projects: Project[]): void {
  localStorage.setItem(STORAGE_KEYS.PROJECTS, JSON.stringify(projects));
}

// Load / Save Criteria
export function loadCriteria(): Criterion[] {
  const data = localStorage.getItem(STORAGE_KEYS.CRITERIA);
  if (data) {
    try {
      return JSON.parse(data);
    } catch {
      // fallback
    }
  }
  return DEFAULT_CRITERIA;
}

export function saveCriteria(criteria: Criterion[]): void {
  localStorage.setItem(STORAGE_KEYS.CRITERIA, JSON.stringify(criteria));
}

// Load / Save Schedule
export function loadSchedule(): ScheduleItem[] {
  const data = localStorage.getItem(STORAGE_KEYS.SCHEDULE);
  if (data) {
    try {
      const parsed = JSON.parse(data);
      if (Array.isArray(parsed) && parsed.length > 0) {
        // Upgrade legacy placeholder schedules (e.g. October dates or old 8/10 items without breakouts)
        const isOldPlaceholder = parsed.some(
          (item: ScheduleItem) =>
            item.date?.includes('October') ||
            item.title === 'Welcome & Shark Tank Rules' ||
            item.title === 'Pitch Block 1: Ministries 1–6'
        );
        if (isOldPlaceholder) {
          saveSchedule(DEFAULT_SCHEDULE);
          return DEFAULT_SCHEDULE;
        }

        return parsed.map((item: ScheduleItem, idx: number) => {
          if (!item.date) {
            const isDay1 = idx < 10;
            return {
              ...item,
              date: isDay1 ? 'Tuesday, September 29, 2026' : 'Wednesday, September 30, 2026',
            };
          }
          return item;
        });
      }
    } catch {
      // fallback
    }
  }
  return DEFAULT_SCHEDULE;
}

export function saveSchedule(schedule: ScheduleItem[]): void {
  localStorage.setItem(STORAGE_KEYS.SCHEDULE, JSON.stringify(schedule));
}

// Load / Save Ballots
export function loadBallots(): Ballot[] {
  const data = localStorage.getItem(STORAGE_KEYS.BALLOTS);
  if (data) {
    try {
      return JSON.parse(data);
    } catch {
      // fallback
    }
  }
  return [];
}

export function saveBallots(ballots: Ballot[]): void {
  localStorage.setItem(STORAGE_KEYS.BALLOTS, JSON.stringify(ballots));
}

export function submitBallot(ballot: Ballot): Ballot[] {
  const ballots = loadBallots();
  // Check if voter already submitted; if so, update their ballot
  const existingIdx = ballots.findIndex((b) => b.voterId === ballot.voterId);
  if (existingIdx >= 0) {
    ballots[existingIdx] = ballot;
  } else {
    ballots.push(ballot);
  }
  saveBallots(ballots);
  return ballots;
}

// Draft scores & feedback while voting
export function loadDraftScores(): Record<string, Record<string, number>> {
  const data = localStorage.getItem(STORAGE_KEYS.DRAFT_SCORES);
  if (data) {
    try {
      return JSON.parse(data);
    } catch {
      // fallback
    }
  }
  return {};
}

export function saveDraftScores(scores: Record<string, Record<string, number>>): void {
  localStorage.setItem(STORAGE_KEYS.DRAFT_SCORES, JSON.stringify(scores));
}

export function loadDraftFeedback(): Record<string, string> {
  const data = localStorage.getItem(STORAGE_KEYS.DRAFT_FEEDBACK);
  if (data) {
    try {
      return JSON.parse(data);
    } catch {
      // fallback
    }
  }
  return {};
}

export function saveDraftFeedback(feedback: Record<string, string>): void {
  localStorage.setItem(STORAGE_KEYS.DRAFT_FEEDBACK, JSON.stringify(feedback));
}

// Personal Notes
export function loadPersonalNotes(): Record<string, string> {
  const data = localStorage.getItem(STORAGE_KEYS.USER_NOTES);
  if (data) {
    try {
      return JSON.parse(data);
    } catch {
      // fallback
    }
  }
  return {};
}

export function savePersonalNote(projectId: string, note: string): void {
  const notes = loadPersonalNotes();
  notes[projectId] = note;
  localStorage.setItem(STORAGE_KEYS.USER_NOTES, JSON.stringify(notes));
}

// Score Calculation Logic with Staff Weighting
export function computeLeaderboard(
  projects: Project[],
  criteria: Criterion[],
  ballots: Ballot[],
  staffMultiplier = 3.0
): ProjectScoreSummary[] {
  const summaries: ProjectScoreSummary[] = projects.map((project) => {
    let totalScore = 0;
    let unweightedTotalScore = 0;
    let totalScoreWeight = 0;
    let unweightedScoreWeight = 0;
    let ballotCount = 0;
    let staffBallotCount = 0;
    let audienceBallotCount = 0;

    const criteriaTotals: Record<string, number> = {};
    const criteriaWeightedTotals: Record<string, number> = {};
    const criteriaCounts: Record<string, number> = {};
    const criteriaWeightSums: Record<string, number> = {};

    criteria.forEach((c) => {
      criteriaTotals[c.id] = 0;
      criteriaWeightedTotals[c.id] = 0;
      criteriaCounts[c.id] = 0;
      criteriaWeightSums[c.id] = 0;
    });

    ballots.forEach((ballot) => {
      // In-progress draft ballots that haven't been submitted yet do not count toward official standings
      if (ballot.isSubmitted === false) return;

      const projScores = ballot.scores[project.id];
      if (projScores) {
        let scoredAny = false;
        const isStaff = ballot.voterRole === 'staff';
        const ballotWeight = isStaff ? (ballot.weight || staffMultiplier) : (ballot.weight || 1.0);

        criteria.forEach((c) => {
          const rawScore = projScores[c.id];
          if (typeof rawScore === 'number' && rawScore > 0) {
            scoredAny = true;
            const critWeight = c.weight || 1.0;
            const effectiveWeight = critWeight * ballotWeight;
            const weightedScore = rawScore * effectiveWeight;

            totalScore += weightedScore;
            unweightedTotalScore += rawScore * critWeight;
            totalScoreWeight += effectiveWeight;
            unweightedScoreWeight += critWeight;

            criteriaTotals[c.id] += rawScore;
            criteriaWeightedTotals[c.id] += weightedScore;
            criteriaCounts[c.id] += 1;
            criteriaWeightSums[c.id] += effectiveWeight;
          }
        });

        if (scoredAny) {
          ballotCount++;
          if (isStaff) {
            staffBallotCount++;
          } else {
            audienceBallotCount++;
          }
        }
      }
    });

    const criteriaAverages: Record<string, number> = {};
    const rawCriteriaAverages: Record<string, number> = {};
    criteria.forEach((c) => {
      const weightSum = criteriaWeightSums[c.id] || 0;
      const count = criteriaCounts[c.id] || 0;
      criteriaAverages[c.id] = weightSum > 0 ? Number((criteriaWeightedTotals[c.id] / weightSum).toFixed(2)) : 0;
      rawCriteriaAverages[c.id] = count > 0 ? Number((criteriaTotals[c.id] / count).toFixed(2)) : 0;
    });

    // Weighted average rating on the 1-5 scale across all criteria
    const averageScore = totalScoreWeight > 0 ? Number((totalScore / totalScoreWeight).toFixed(2)) : 0;
    const unweightedAverageScore = unweightedScoreWeight > 0 ? Number((unweightedTotalScore / unweightedScoreWeight).toFixed(2)) : 0;

    return {
      project,
      totalScore: Number(totalScore.toFixed(1)),
      unweightedTotalScore: Number(unweightedTotalScore.toFixed(1)),
      averageScore,
      unweightedAverageScore,
      ballotCount,
      staffBallotCount,
      audienceBallotCount,
      criteriaAverages,
      rawCriteriaAverages,
      rank: 0,
    };
  });

  // Sort descending by totalScore (or averageScore if ties)
  summaries.sort((a, b) => {
    if (b.totalScore !== a.totalScore) {
      return b.totalScore - a.totalScore;
    }
    return b.averageScore - a.averageScore;
  });

  // Assign ranks
  summaries.forEach((s, idx) => {
    s.rank = idx + 1;
  });

  return summaries;
}

// Generate demo test ballots for rehearsal with staff and audience mix
export function seedSampleBallots(
  projects: Project[], 
  criteria: Criterion[], 
  count = 18,
  staffMultiplier = 3.0
): Ballot[] {
  const sampleVoters = [
    { name: 'Pastor Marcus Bell (Executive Staff)', role: 'staff' as const },
    { name: 'Sister Angela Hayes (Ministry Director)', role: 'staff' as const },
    { name: 'Elder Thomas Ruiz (Board Member)', role: 'staff' as const },
    { name: 'Rachel Sterling (Network Lead)', role: 'staff' as const },
    { name: 'David Zimmerman', role: 'audience' as const },
    { name: 'Bro. Kevin Washington', role: 'audience' as const },
    { name: 'Hannah Choi', role: 'audience' as const },
    { name: 'Samuel O\'Connor', role: 'audience' as const },
    { name: 'Grace Mwangi', role: 'audience' as const },
    { name: 'Dr. Nathan Cooper', role: 'audience' as const },
    { name: 'Chloe Bennett', role: 'audience' as const },
    { name: 'Jeremiah King', role: 'audience' as const },
    { name: 'Leah Campbell', role: 'audience' as const },
    { name: 'Aaron Goldstein', role: 'audience' as const },
    { name: 'Priscilla Vance', role: 'audience' as const },
    { name: 'Joshua Miller', role: 'audience' as const },
    { name: 'Bethany Adams', role: 'audience' as const },
    { name: 'Caleb Wright', role: 'audience' as const }
  ];

  const ballots: Ballot[] = [];

  for (let i = 0; i < count; i++) {
    const voter = sampleVoters[i % sampleVoters.length];
    const voterName = voter.name + (i >= sampleVoters.length ? ` (${i + 1})` : '');
    const isStaff = voter.role === 'staff';
    const weight = isStaff ? staffMultiplier : 1.0;
    const scores: Record<string, Record<string, number>> = {};
    const feedback: Record<string, string> = {};

    projects.forEach((proj) => {
      scores[proj.id] = {};
      criteria.forEach((crit) => {
        const randomBonus = Math.sin(proj.order * 1.5 + crit.id.charCodeAt(1)) * 1.2;
        const score = Math.max(1, Math.min(5, Math.round(3.8 + randomBonus + (Math.random() * 1.2 - 0.6))));
        scores[proj.id][crit.id] = score;
      });
      if (Math.random() > 0.5) {
        feedback[proj.id] = `Incredible heart for ${proj.title}! Very impressed with the stewardship.`;
      }
    });

    ballots.push({
      id: `ballot_seed_${i + 1}`,
      voterId: `voter_seed_${i + 1}`,
      voterName,
      voterRole: voter.role,
      weight,
      submittedAt: new Date(Date.now() - (count - i) * 120000).toISOString(),
      scores,
      feedback,
      isSubmitted: true,
    });
  }

  saveBallots(ballots);
  return ballots;
}

// CSV Exporter with staff/audience breakdown
export function exportResultsToCSV(
  projects: Project[],
  criteria: Criterion[],
  ballots: Ballot[],
  staffMultiplier = 3.0
): string {
  const summaries = computeLeaderboard(projects, criteria, ballots, staffMultiplier);

  const headers = [
    'Rank',
    'Ministry Title',
    'Leader / Presenter',
    'Website',
    'Total Weighted Score',
    'Unweighted Raw Points',
    'Staff Ballots',
    'Audience Ballots',
    'Total Ballots Received',
    'Weighted Avg Rating (1-5)',
    'Unweighted Avg Rating (1-5)',
    ...criteria.map((c) => `${c.name} (Avg / 5)`),
  ];

  const rows = summaries.map((s) => [
    s.rank,
    `"${s.project.title.replace(/"/g, '""')}"`,
    `"${s.project.presenter.replace(/"/g, '""')}"`,
    `"${(s.project.website || '').replace(/"/g, '""')}"`,
    s.totalScore,
    s.unweightedTotalScore,
    s.staffBallotCount,
    s.audienceBallotCount,
    s.ballotCount,
    s.averageScore,
    s.unweightedAverageScore,
    ...criteria.map((c) => s.criteriaAverages[c.id] || 0),
  ]);

  const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  return csvContent;
}
